import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useV2Auth } from '@/context/V2AuthContext';
import { X, Heart, Send, Link, Copy, Check, Share2, Users, Instagram, Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export default function GiveV2() {
  const { user, API_V2 } = useV2Auth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [recent, setRecent] = useState([]);
  const [community, setCommunity] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [profileUser, setProfileUser] = useState(null);
  const [message, setMessage] = useState('');
  const [context, setContext] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [sentPhase, setSentPhase] = useState(null);
  const [sentCount, setSentCount] = useState(0);
  const [shareText, setShareText] = useState('');
  const [sharing, setSharing] = useState(false);
  const [sendMode, setSendMode] = useState('direct');
  const [linkRecipient, setLinkRecipient] = useState('');
  const [generatedLink, setGeneratedLink] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);

  useEffect(() => {
    if (!user) { navigate('/auth'); return; }
    let cancelled = false;
    async function loadData() {
      try {
        const [recentRes, communityRes] = await Promise.all([
          axios.get(`${API_V2}/users/recent`, { withCredentials: true }),
          axios.get(`${API_V2}/users/community`, { withCredentials: true }),
        ]);
        if (!cancelled) { setRecent(recentRes.data || []); setCommunity(communityRes.data || []); }
      } catch (err) {
        if (!cancelled) {
          if (err.response?.status === 401) { navigate('/auth', { replace: true }); return; }
          setRecent([]); setCommunity([]);
        }
      }
    }
    loadData();
    return () => { cancelled = true; };
  }, [user, API_V2, navigate]);

  useEffect(() => {
    if (location.state?.echoContext) setContext(location.state.echoContext.slice(0, 200));
    if (location.state?.prefillMessage) setMessage(location.state.prefillMessage.slice(0, 800));
    if (location.state?.preselectUser) {
      const u = location.state.preselectUser;
      setSelectedUsers((prev) => (prev.some((s) => s.id === u.id) ? prev : [...prev, u]));
    }
  }, [location.state]);

  const canSubmit = message.trim().length > 0 && message.trim().length <= 800 && context.length <= 200;

  /* ── Multi-Select helpers ── */
  const toggleUser = (u) => {
    setSelectedUsers((prev) => {
      const exists = prev.find((s) => s.id === u.id);
      if (exists) return prev.filter((s) => s.id !== u.id);
      setQuery('');
      setResults([]);
      return [...prev, u];
    });
  };

  const removeUser = (userId) => {
    setSelectedUsers((prev) => prev.filter((s) => s.id !== userId));
  };

  const isUserSelected = (userId) => selectedUsers.some((s) => s.id === userId);

  const openProfile = async (userId) => {
    try { const res = await axios.get(`${API_V2}/users/${userId}/public`, { withCredentials: true }); setProfileUser(res.data); }
    catch (err) { console.error('Profile fetch failed:', err); setProfileUser(null); }
  };

  const selectFromBubble = (u) => { toggleUser(u); };

  const runSearch = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.trim().length < 2) { setResults([]); return; }
    setSearching(true); setError(null);
    try { const res = await axios.get(`${API_V2}/users/search`, { params: { query: searchQuery.trim() }, withCredentials: true }); setResults(res.data || []); }
    catch (err) { setError(err.response?.data?.detail || t('give.sendError')); }
    finally { setSearching(false); }
  }, [API_V2, t]);

  useEffect(() => {
    const timer = setTimeout(() => { runSearch(query); }, 300);
    return () => clearTimeout(timer);
  }, [query, runSearch]);

  const sendInvite = async () => {
    if (!query || query.trim().length < 3) return;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(query.trim())) { setError(t('give.emailInvalid')); return; }
    if (!message.trim()) { setError(t('give.messageRequired')); return; }
    setSubmitting(true); setError(null);
    try {
      await axios.post(`${API_V2}/invites`, { email: query.trim(), previewText: message.trim().slice(0, 280), senderName: user?.name || undefined }, { withCredentials: true });
      navigate('/inbox', { replace: true });
    } catch (err) {
      if (err.response?.status === 401) { navigate('/auth', { replace: true }); return; }
      setError(err.response?.data?.detail || t('give.inviteFailed'));
    }
    finally { setSubmitting(false); }
  };

  /* ── Submit Carma to all selected users ── */
  const submitCarma = async (e) => {
    e.preventDefault();
    if (!canSubmit || selectedUsers.length === 0) return;
    setSubmitting(true); setError(null);
    try {
      const promises = selectedUsers.map((u) =>
        axios.post(`${API_V2}/carma`, { receiverId: u.id, message: message.trim(), context: context.trim() || undefined }, { withCredentials: true })
      );
      await Promise.all(promises);
      setSentCount(selectedUsers.length);
      setSentPhase('confirm');
    } catch (err) {
      if (err.response?.status === 401) { navigate('/auth', { replace: true }); return; }
      setError(err.response?.data?.detail || t('give.sendError'));
    }
    finally { setSubmitting(false); }
  };

  const submitShareLink = async () => {
    if (!message.trim() || !linkRecipient.trim()) return;
    setSubmitting(true); setError(null);
    try {
      const res = await axios.post(`${API_V2}/carma/share`, { recipientName: linkRecipient.trim(), message: message.trim(), context: context.trim() || undefined }, { withCredentials: true });
      setGeneratedLink(`${window.location.origin}${res.data.shareUrl}`);
    } catch (err) {
      if (err.response?.status === 401) { navigate('/auth', { replace: true }); return; }
      setError(err.response?.data?.detail || t('give.linkError'));
    }
    finally { setSubmitting(false); }
  };

  const copyLink = async () => {
    if (!generatedLink) return;
    try { await navigator.clipboard.writeText(generatedLink); } catch (_e) {
      const input = document.createElement('input'); input.value = generatedLink; document.body.appendChild(input); input.select(); document.execCommand('copy'); document.body.removeChild(input);
    }
    setLinkCopied(true); setTimeout(() => setLinkCopied(false), 2500);
  };

  const shareViaWhatsApp = () => {
    if (!generatedLink) return;
    const text = t('shareLink.whatsappText', { link: generatedLink });
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const shareViaInstagram = () => {
    if (!generatedLink) return;
    const text = t('shareLink.instagramText', { name: linkRecipient || '', link: generatedLink });
    navigator.clipboard.writeText(text).catch(() => {});
    setLinkCopied(true); setTimeout(() => setLinkCopied(false), 2500);
    window.open('https://www.instagram.com/direct/inbox/', '_blank');
  };

  const shareNative = async () => {
    if (!generatedLink || !navigator.share) return;
    try { await navigator.share({ title: t('shareLink.nativeTitle'), text: t('shareLink.nativeText'), url: generatedLink }); } catch (_e) { /* user cancelled sharing dialog */ }
  };

  const submitShare = async () => {
    if (!shareText.trim()) return;
    setSharing(true);
    try { await axios.post(`${API_V2}/impulse/share`, { text: shareText.trim(), anonymous: false }, { withCredentials: true }); navigate('/inbox', { replace: true }); }
    catch (err) { console.error('Submit share failed:', err); navigate('/inbox', { replace: true }); }
    finally { setSharing(false); }
  };

  if (!user) return null;

  // ── Link Generated Phase ──
  if (generatedLink) {
    return (
      <div className="min-h-screen text-[var(--text)] flex items-center justify-center px-4">
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-8 sm:p-10 shadow-sm max-w-md w-full text-center space-y-6" data-testid="share-link-result">
          <div className="w-14 h-14 mx-auto rounded-full bg-[var(--primary)]/10 flex items-center justify-center"><Link size={28} className="text-[var(--primary)]" /></div>
          <div><h2 className="text-xl font-semibold mb-2">{t('shareLink.title')}</h2><p className="text-sm text-[var(--muted)]">{t('shareLink.subtitle', { name: linkRecipient })}</p></div>
          <div className="bg-[var(--surface)] rounded-xl border border-[var(--border)] p-3 flex items-center gap-2">
            <input type="text" value={generatedLink} readOnly data-testid="share-link-input" className="flex-1 bg-transparent text-xs text-[var(--text)] outline-none truncate" />
            <button onClick={copyLink} data-testid="copy-link-btn" className={`flex-shrink-0 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${linkCopied ? 'bg-green-100 text-green-700' : 'bg-[var(--primary)] text-[var(--primary-contrast)] hover:opacity-90'}`}>
              {linkCopied ? <><Check size={12} className="inline mr-1" />{t('shareLink.copied')}</> : <><Copy size={12} className="inline mr-1" />{t('shareLink.copy')}</>}
            </button>
          </div>
          <div className="flex gap-2">
            <button onClick={shareViaWhatsApp} data-testid="share-whatsapp-btn" className="flex-1 rounded-full bg-[#25D366] text-white py-2.5 text-sm font-medium hover:opacity-90 inline-flex items-center justify-center gap-1.5">{t('give.shareWhatsapp')}</button>
            <button onClick={shareViaInstagram} data-testid="share-instagram-btn" className="flex-1 rounded-full bg-gradient-to-r from-[#833AB4] via-[#E1306C] to-[#F77737] text-white py-2.5 text-sm font-medium hover:opacity-90 inline-flex items-center justify-center gap-1.5">{t('give.shareInstagram')}</button>
          </div>
          <div className="flex gap-2">
            {typeof navigator !== 'undefined' && navigator.share && (
              <button onClick={shareNative} data-testid="share-native-btn" className="flex-1 rounded-full bg-[var(--surface)] border border-[var(--border)] py-2.5 text-sm font-medium hover:bg-[var(--bg)] inline-flex items-center justify-center gap-1.5"><Share2 size={14} /> {t('shareLink.share')}</button>
            )}
          </div>
          <div className="space-y-2 pt-2">
            <button onClick={() => { setGeneratedLink(null); setMessage(''); setLinkRecipient(''); setContext(''); }} className="w-full rounded-full border border-[var(--border)] bg-[var(--surface)] py-2.5 text-sm font-medium hover:bg-[var(--bg)] text-[var(--muted)]">{t('shareLink.another')}</button>
            <button onClick={() => navigate('/inbox', { replace: true })} className="w-full rounded-full border border-[var(--border)] bg-white/70 py-2 text-xs text-[var(--muted)] hover:bg-white/90">{t('shareLink.toInbox')}</button>
          </div>
        </div>
      </div>
    );
  }

  // ── Post-Send Phase: Confirmation ──
  if (sentPhase === 'confirm') {
    const isSingle = sentCount === 1;
    return (
      <div className="min-h-screen text-[var(--text)] flex items-center justify-center px-4">
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-8 sm:p-10 shadow-sm max-w-md w-full text-center space-y-6">
          <div className="w-14 h-14 mx-auto rounded-full bg-[var(--primary)]/10 flex items-center justify-center">
            {isSingle ? <Heart size={28} className="text-[var(--primary)]" /> : <Users size={28} className="text-[var(--primary)]" />}
          </div>
          <div>
            <h2 className="text-xl font-semibold mb-2" data-testid="sent-confirm-title">
              {isSingle ? t('give.confirmSentTitle') : t('give.groupSentTitle', { count: sentCount })}
            </h2>
            <p className="text-sm text-[var(--muted)]">
              {isSingle ? t('give.confirmSentText') : t('give.groupSentText', { count: sentCount })}
            </p>
          </div>
          <div className="space-y-3">
            <button type="button" onClick={() => setSentPhase('share')} data-testid="share-thought-btn" className="w-full rounded-full bg-[var(--primary)] text-[var(--primary-contrast)] py-2.5 text-sm font-medium hover:opacity-90">{t('give.shareThoughtBtn')}</button>
            <button type="button" onClick={() => navigate('/inbox', { replace: true })} className="w-full rounded-full border border-[var(--border)] bg-[var(--surface)] py-2.5 text-sm font-medium hover:bg-[var(--bg)] text-[var(--muted)]">{t('give.toInbox')}</button>
          </div>
        </div>
      </div>
    );
  }

  // ── Post-Send Phase: Share a thought ──
  if (sentPhase === 'share') {
    return (
      <div className="min-h-screen text-[var(--text)] flex items-center justify-center px-4">
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-8 sm:p-10 shadow-sm max-w-md w-full space-y-6">
          <div className="text-center">
            <h2 className="text-lg font-semibold mb-1">{t('give.shareThoughtTitle')}</h2>
            <p className="text-xs text-[var(--muted)]">{t('give.shareThoughtHint')}</p>
          </div>
          <div>
            <textarea value={shareText} onChange={(e) => setShareText(e.target.value.slice(0, 120))} placeholder={t('give.shareThoughtPlaceholder')} data-testid="share-thought-input" className="w-full rounded-xl border border-[var(--border)] bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--focus-ring)] resize-none" rows={3} />
            <p className="text-[10px] text-[var(--muted)] text-right mt-1">{shareText.length}/120</p>
          </div>
          <div className="space-y-3">
            <button type="button" onClick={submitShare} disabled={!shareText.trim() || sharing} data-testid="submit-share-btn" className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[var(--primary)] text-[var(--primary-contrast)] py-2.5 text-sm font-medium hover:opacity-90 disabled:opacity-50"><Send size={15} />{sharing ? t('feed.posting') : t('feed.postBtn')}</button>
            <button type="button" onClick={() => navigate('/inbox', { replace: true })} className="w-full rounded-full border border-[var(--border)] bg-[var(--surface)] py-2.5 text-sm font-medium hover:bg-[var(--bg)] text-[var(--muted)]">{t('give.skipBtn')}</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-[var(--text)]">
      <div className="max-w-6xl mx-auto px-4 py-6 sm:py-8">
        <div className="flex flex-col lg:flex-row gap-6">
          <div className="flex-1 min-w-0">
            <div className="bg-white/85 backdrop-blur-sm rounded-2xl p-6 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl font-semibold">{t('give.title')}</h1>
            <p className="text-xs sm:text-sm text-[var(--muted)]">{t('give.subtitle')}</p>
          </div>
          <button type="button" onClick={() => navigate('/profile')} className="self-start rounded-full bg-[var(--surface)] border border-[var(--border)] px-3 sm:px-4 py-1.5 sm:py-2 text-[10px] sm:text-xs font-medium hover:bg-[var(--bg)]">{t('give.backToProfile')}</button>
        </div>

        {/* Disclaimer */}
        <div className="mb-6 p-4 rounded-xl border border-[var(--border)] bg-white/70 text-xs sm:text-sm text-[var(--muted)] space-y-3">
          <p><strong className="text-[var(--text)]">CarmaCircle</strong> – {t('give.disclaimerP1')}</p>
          <p>{t('give.disclaimerP2')}</p>
          <p>{t('give.disclaimerP3')} <strong className="text-[var(--text)]">{t('give.disclaimerBold')}</strong></p>
        </div>

        {/* Mode Switcher */}
        <div className="flex rounded-full border border-[var(--border)] bg-[var(--surface)] p-1 mb-6">
          <button type="button" onClick={() => { setSendMode('direct'); setError(null); }} data-testid="mode-direct-btn" className={`flex-1 rounded-full px-3 py-2 text-xs font-medium transition-colors inline-flex items-center justify-center gap-1.5 ${sendMode === 'direct' ? 'bg-white shadow-sm text-[var(--text)]' : 'text-[var(--muted)] hover:text-[var(--text)]'}`}>
            <Users size={13} />
            {t('give.modeDirect')}
          </button>
          <button type="button" onClick={() => { setSendMode('link'); setError(null); setSelectedUsers([]); }} data-testid="mode-link-btn" className={`flex-1 rounded-full px-3 py-2 text-xs font-medium transition-colors inline-flex items-center justify-center gap-1.5 ${sendMode === 'link' ? 'bg-white shadow-sm text-[var(--text)]' : 'text-[var(--muted)] hover:text-[var(--text)]'}`}>
            <Globe size={13} />
            {t('give.modeLink')}
          </button>
        </div>

        {sendMode === 'link' ? (
          <div className="space-y-6">
            {/* Social Media Explainer */}
            <div className="rounded-xl border border-[var(--primary)]/20 bg-[var(--primary)]/5 p-4 space-y-2" data-testid="social-sharing-hint">
              <div className="flex items-start gap-3">
                <div className="flex gap-1.5 flex-shrink-0 pt-0.5">
                  <Instagram size={16} className="text-[var(--primary)]" />
                  <Share2 size={16} className="text-[var(--primary)]" />
                </div>
                <div>
                  <p className="text-sm text-[var(--text)] leading-relaxed">{t('give.linkSocialHint')}</p>
                  <p className="text-[11px] text-[var(--primary)] font-medium mt-1.5">{t('give.linkSocialSteps')}</p>
                </div>
              </div>
            </div>
            <section className="border border-[var(--border)] rounded-xl bg-[var(--surface)] p-4 space-y-4">
              <h2 className="text-sm font-medium">{t('give.linkRecipientTitle')}</h2>
              <p className="text-[10px] text-[var(--muted)]">{t('give.linkRecipientHint')}</p>
              <input type="text" value={linkRecipient} onChange={(e) => setLinkRecipient(e.target.value)} placeholder={t('give.linkRecipientPlaceholder')} data-testid="link-recipient-input" className="w-full rounded-md border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]" />
            </section>
            <section className="border border-[var(--border)] rounded-xl bg-[var(--surface)] p-4 space-y-4">
              <h2 className="text-sm font-medium">{t('give.linkMessageTitle')}</h2>
              <div className="space-y-1 text-sm">
                <label className="block font-medium">{t('give.messageLabel')}</label>
                <p className="text-[10px] text-[var(--muted)] mb-1">{t('give.messageHint')}</p>
                <textarea value={message} onChange={(e) => setMessage(e.target.value)} maxLength={800} rows={4} data-testid="link-message-input" className="w-full rounded-md border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]" />
                <div className="text-[11px] text-[var(--muted)]">{message.length}/800 {t('give.chars')}</div>
              </div>
              <div className="space-y-1 text-sm">
                <label className="block font-medium">{t('give.contextLabel')}</label>
                <input type="text" value={context} onChange={(e) => setContext(e.target.value)} maxLength={200} className="w-full rounded-md border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]" />
              </div>
            </section>
            {error && <div className="text-xs text-[var(--danger)]">{error}</div>}
            <div className="flex justify-end">
              <button type="button" onClick={submitShareLink} disabled={submitting || !message.trim() || !linkRecipient.trim()} data-testid="create-share-link-btn" className="rounded-full bg-[var(--primary)] text-[var(--primary-contrast)] px-6 py-2.5 text-sm font-medium hover:opacity-90 disabled:opacity-60 inline-flex items-center gap-2"><Share2 size={14} />{submitting ? t('give.linkCreating') : t('give.linkCreateBtn')}</button>
            </div>
          </div>
        ) : (
        <form onSubmit={submitCarma} className="space-y-6">
          {/* ── Recipients Section with Multi-Select ── */}
          <section className="border border-[var(--border)] rounded-xl bg-[var(--surface)] p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-medium">{t('give.recipientSection')}</h2>
              <p className="text-[10px] text-[var(--muted)]">{t('give.multiSelectHint')}</p>
            </div>

            {/* Selected users as chips */}
            {selectedUsers.length > 0 && (
              <div className="flex flex-wrap gap-1.5" data-testid="selected-chips">
                {selectedUsers.map((u) => (
                  <span key={u.id} className="inline-flex items-center gap-1 bg-[var(--primary)]/10 text-[var(--primary)] rounded-full pl-2.5 pr-1 py-1 text-xs font-medium" data-testid={`chip-${u.id}`}>
                    <span className="w-5 h-5 rounded-full bg-[var(--primary)] text-[var(--primary-contrast)] flex items-center justify-center text-[10px] font-bold flex-shrink-0">{(u.name || '?')[0].toUpperCase()}</span>
                    {u.name || t('give.unknown')}
                    <button type="button" onClick={() => removeUser(u.id)} className="ml-0.5 w-5 h-5 rounded-full hover:bg-[var(--primary)]/20 flex items-center justify-center" title={t('give.removeRecipient')}><X size={12} /></button>
                  </span>
                ))}
                <span className="text-[10px] text-[var(--primary)] font-medium self-center px-1">{t('give.selectedCount', { count: selectedUsers.length })}</span>
              </div>
            )}

            <div className="relative">
              <input type="text" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('give.searchPlaceholder')} data-testid="search-input" className="w-full rounded-md border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]" />
              {searching && <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[var(--muted)]">{t('give.searching')}</div>}
            </div>
            <p className="text-[10px] text-[var(--muted)] mt-1" data-testid="search-email-hint">{t('give.searchEmailHint')}</p>

            {/* Email detected — person not registered */}
            {/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(query.trim()) && results.length === 0 && !searching && (
              <div className="mt-3 rounded-xl border border-[var(--primary)]/20 bg-[var(--primary)]/5 p-3 text-xs text-[var(--text)]" data-testid="email-invite-notice">
                {t('give.emailDetected', { email: query.trim() })}
              </div>
            )}

            {/* Search results */}
            {results.length > 0 && (
              <div className="mt-3 space-y-1">
                {results.map((u) => {
                  const sel = isUserSelected(u.id);
                  return (
                    <button key={u.id} type="button" onClick={() => toggleUser(u)} data-testid={`result-${u.id}`} className={`w-full flex items-center justify-between rounded-md border px-3 py-2 text-left text-sm transition-colors ${sel ? 'border-[var(--primary)] bg-[var(--primary)]/5' : 'border-[var(--border)] bg-[var(--surface)] hover:bg-white'}`}>
                      <span className="flex items-center gap-2">
                        <span className="w-7 h-7 rounded-full bg-[var(--primary)] text-[var(--primary-contrast)] flex items-center justify-center text-xs font-bold flex-shrink-0">{(u.name || '?')[0].toUpperCase()}</span>
                        <span className="font-medium">{u.name || t('give.unknown')}</span>
                      </span>
                      {sel && <Check size={16} className="text-[var(--primary)]" />}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Recent recipients */}
            {results.length === 0 && query.length < 2 && recent.length > 0 && (
              <div className="mt-3 space-y-1">
                <p className="text-xs text-[var(--muted)] mb-1">{t('give.recentLabel')}</p>
                {recent.map((u) => {
                  const sel = isUserSelected(u.id);
                  return (
                    <button key={u.id} type="button" onClick={() => toggleUser(u)} className={`w-full flex items-center justify-between rounded-md border px-3 py-2 text-left text-sm transition-colors ${sel ? 'border-[var(--primary)] bg-[var(--primary)]/5' : 'border-[var(--border)] bg-[var(--surface)] hover:bg-white'}`}>
                      <span className="flex items-center gap-2">
                        <span className="w-7 h-7 rounded-full bg-[var(--primary)] text-[var(--primary-contrast)] flex items-center justify-center text-xs font-bold flex-shrink-0">{(u.name || '?')[0].toUpperCase()}</span>
                        <span className="font-medium">{u.name || t('give.unknown')}</span>
                      </span>
                      {sel && <Check size={16} className="text-[var(--primary)]" />}
                    </button>
                  );
                })}
              </div>
            )}
          </section>

          {/* Message Section */}
          <section className="border border-[var(--border)] rounded-xl bg-[var(--surface)] p-4 space-y-4">
            <h2 className="text-sm font-medium">{t('give.linkMessageTitle')}</h2>
            <div className="space-y-1 text-sm">
              <label className="block font-medium">{t('give.messageLabel')}</label>
              <p className="text-[10px] text-[var(--muted)] mb-1">{t('give.messageHint')}</p>
              <textarea value={message} onChange={(e) => setMessage(e.target.value)} maxLength={800} required rows={4} data-testid="message-input" className="w-full rounded-md border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]" />
              <div className="text-[11px] text-[var(--muted)]">{message.length}/800 {t('give.chars')}</div>
            </div>
            <div className="space-y-1 text-sm">
              <label className="block font-medium">{t('give.contextLabel')}</label>
              <input type="text" value={context} onChange={(e) => setContext(e.target.value)} maxLength={200} className="w-full rounded-md border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]" />
              <div className="text-[11px] text-[var(--muted)]">{context.length}/200 {t('give.chars')}</div>
            </div>
          </section>

          {error && <div className="text-xs text-[var(--danger)]">{error}</div>}

          <div className="flex flex-col sm:flex-row justify-end gap-2">
            <button type="button" onClick={sendInvite} disabled={submitting || !message.trim() || query.trim().length < 3} data-testid="invite-by-email-btn" className="rounded-full bg-[var(--surface)] border border-[var(--border)] px-5 py-2 text-sm font-medium text-[var(--muted)] hover:bg-[var(--bg)] disabled:opacity-60">{t('give.inviteByEmail')}</button>
            <button type="submit" disabled={!canSubmit || submitting || selectedUsers.length === 0} data-testid="send-carma-btn" className="rounded-full bg-[var(--primary)] text-[var(--primary-contrast)] px-5 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-60 inline-flex items-center gap-2">
              {selectedUsers.length > 1 && <Users size={14} />}
              {submitting ? t('give.sending') : selectedUsers.length > 1 ? `${t('give.send')} (${selectedUsers.length})` : t('give.send')}
            </button>
          </div>
        </form>
        )}
        </div>
          </div>

          {/* Community Bubble Cloud */}
          {community.length > 0 && sendMode === 'direct' && (
            <div className="lg:w-72 xl:w-80 flex-shrink-0">
              <div className="bg-white/85 backdrop-blur-sm rounded-2xl p-5 shadow-sm sticky top-24">
                <h3 className="text-sm font-semibold mb-1">{t('give.communityTitle')}</h3>
                <p className="text-[10px] text-[var(--muted)] mb-4">{t('give.communityHint')}</p>
                <div className="flex flex-wrap gap-1.5">
                  {community.map((u) => {
                    const isActive = isUserSelected(u.id);
                    const size = u.receivedCount >= 3 ? 'text-sm font-semibold' : u.receivedCount >= 1 ? 'text-xs font-medium' : 'text-[11px]';
                    return (
                      <span key={u.id} className="inline-flex items-center gap-0.5">
                        <button type="button" onClick={() => selectFromBubble(u)} data-testid={`bubble-${u.id}`} className={`px-2.5 py-1 rounded-full transition-all duration-200 ${size} ${isActive ? 'bg-[var(--primary)] text-[var(--primary-contrast)] shadow-md scale-105' : 'bg-[var(--surface)] border border-[var(--border)] text-[var(--text)] hover:bg-[var(--primary)]/10 hover:border-[var(--primary)]/30 hover:scale-105'}`}>
                          {u.name}
                          {u.receivedCount > 0 && <span className={`ml-1 ${isActive ? 'opacity-80' : 'text-[var(--muted)]'}`}>{u.receivedCount}</span>}
                        </button>
                        <button type="button" onClick={() => openProfile(u.id)} className="w-5 h-5 rounded-full bg-[var(--surface)] border border-[var(--border)] text-[var(--muted)] hover:text-[var(--text)] hover:bg-white flex items-center justify-center text-[9px] font-bold transition-colors" title={t('give.profileView')}>i</button>
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Public Profile Popup */}
      {profileUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={() => setProfileUser(null)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6 relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setProfileUser(null)} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-[var(--surface)] flex items-center justify-center hover:bg-gray-200" data-testid="close-profile-popup"><X size={16} /></button>
            <div className="text-center mb-5">
              <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-[var(--primary)] text-[var(--primary-contrast)] flex items-center justify-center text-2xl font-bold">{(profileUser.name || '?')[0].toUpperCase()}</div>
              <h2 className="text-lg font-semibold">{profileUser.name}</h2>
              {profileUser.memberSince && <p className="text-xs text-[var(--muted)] mt-1">{t('give.memberSince', { date: new Date(profileUser.memberSince).toLocaleDateString(undefined, { month: 'long', year: 'numeric' }) })}</p>}
            </div>
            <div className="grid grid-cols-2 gap-3 mb-5">
              <div className="bg-[var(--surface)] rounded-xl p-3 text-center"><div className="text-2xl font-bold text-[var(--primary)]">{profileUser.receivedCount}</div><div className="text-[10px] text-[var(--muted)] mt-0.5">{t('give.receivedCount')}</div></div>
              <div className="bg-[var(--surface)] rounded-xl p-3 text-center"><div className="text-2xl font-bold text-[var(--primary)]">{profileUser.sentCount}</div><div className="text-[10px] text-[var(--muted)] mt-0.5">{t('give.sentCount')}</div></div>
            </div>
            {!profileUser.isOwnProfile && (
              <button type="button" onClick={() => { toggleUser({ id: profileUser.id, name: profileUser.name }); setProfileUser(null); }} data-testid="send-carma-from-profile" className="w-full rounded-full bg-[var(--primary)] text-[var(--primary-contrast)] py-2.5 text-sm font-medium hover:opacity-90">{t('give.sendToUser', { name: profileUser.name })}</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
